package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.JustifySelf
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.zest.insan_360_website.components.layouts.assessmentWordLibrary
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AssessmentPageState
import org.zest.insan_360_website.pages.Answer
import org.zest.insan_360_website.pages.USER_QUESTION_INDEX_KEY
import kotlin.math.min

@Composable
fun QuestionsIndexSelect(
    answers: List<Answer>,
    questionIndex: Int
) {
    var rowsCount by remember { mutableStateOf(6) }

    DisposableEffect(Unit) {
        val updateRows = {
            val h = window.innerHeight
            val w = window.innerWidth
            
            var targetRows = when {
                w >= 1440 -> 12
                w >= 992 -> 4
                else -> 6
            }
            
            if (h < 680) targetRows = min(targetRows, 4)
            else if (h < 860) targetRows = min(targetRows, 6)
            else if (h < 1080) targetRows = min(targetRows, 8)
            
            rowsCount = targetRows
        }
        
        val resizeListener: (org.w3c.dom.events.Event) -> Unit = { updateRows() }
        
        updateRows()
        window.addEventListener("resize", resizeListener)
        
        onDispose {
            window.removeEventListener("resize", resizeListener)
        }
    }

    Div(
        attrs = Modifier
            .gap(0.5.cssRem)
            .padding(1.cssRem)
            .border {
                width(0.25.cssRem)
                style(LineStyle.Solid)
            }
            .backgroundColor(Colors.AliceBlue)
            .borderRadius(topLeft = 1.5.cssRem, bottomLeft = 1.5.cssRem, bottomRight = 1.5.cssRem)
            .gridAutoFlow(GridAutoFlow.Column)
            .grid { rows { repeat(rowsCount) { size(1.fr) } } }
            .then(QuestionIndexSelectionStyle.toModifier())
            .toAttrs()
    ) {
        if (!answers.isEmpty()) {
            answers.forEachIndexed { index, answer ->
                val isAnswered = answer.isAnswered()
                Box(
                    modifier = Modifier
                        .backgroundColor(
                            if (isAnswered) Colors.Green else Colors.DarkGray
                        )
                        .onClick {
                            sessionStorage.setItem(USER_QUESTION_INDEX_KEY, index)
                            AssessmentPageState.index = index
                        }
                        .then(IndexButtonStyle.toModifier())
                        .thenIf(questionIndex == index) {
                            Modifier.border {
                                width(0.2.cssRem)
                                style(LineStyle.Solid)
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    if (isAnswered) {
                        FaCheck(Modifier.color(Colors.White))
                    } else {
                        SpanText(
                            modifier = Modifier
                                .color(Colors.White)
                                .fontWeight(FontWeight.Bold),
                            text = (index + 1).toString()
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun QuestionsIndexLabel(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .width(2.5.cssRem)
            .padding(topBottom = 1.cssRem)
            .backgroundColor(Colors.Black)
            .borderRadius {
                topRight(0.75.cssRem)
                bottomRight(0.75.cssRem)
            }
            .onClick {
                onClick()
            }
            .cursor(Cursor.Pointer),
        contentAlignment = Alignment.Center

    ) {
        SpanText(
            modifier = Modifier
                .color(Colors.White)
                .fontSize(1.5.cssRem)
                .fontWeight(FontWeight.Bold)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("writing-mode", "vertical-rl")
                },
            text = assessmentWordLibrary.questions_index.localized
        )
    }
}

val IndexButtonStyle = CssStyle {
    base {
        Modifier
            .size(2.9.cssRem)
            .fontSize(1.25.cssRem)
            .borderRadius(0.5.cssRem)
            .boxShadow(offsetY = 0.25.cssRem)
            .cursor(Cursor.Pointer)
    }
    Breakpoint.MD {
        Modifier
            .size(3.2.cssRem)
            .fontSize(1.35.cssRem)
    }
    Breakpoint.LG {
        Modifier
            .size(3.6.cssRem)
            .fontSize(1.45.cssRem)
    }
    Breakpoint.XL {
        Modifier
            .size(4.0.cssRem)
            .fontSize(1.5.cssRem)
    }

    active {
        Modifier
            .translate(tx = 0.px, ty = 0.25.cssRem)
            .scale(0.95)
    }
}

val QuestionIndexSelectionStyle = CssStyle {
    base {
        Modifier
            .fontSize(2.cssRem)
    }
}

@OptIn(ExperimentalComposeWebApi::class)
val QuestionIndexContainerStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Row)
            .justifySelf(JustifySelf.Start)
            .position(Position.Absolute)
            .left(0.px)
            .styleModifier {
                property("transform", "translateX(calc(-100% + 2.5rem))")
                transitions {
                    "transform" {
                        duration(300.ms)
                        timingFunction(AnimationTimingFunction.EaseOut)
                    }
                }
            }
            .fontSize(0.75.cssRem)
    }

    Breakpoint.LG {
        Modifier
            .fontSize(1.cssRem)
    }

    Breakpoint.XXL {
        Modifier
            .justifySelf(JustifySelf.Start)
            .margin(left = 3.cssRem)
            .styleModifier {
                property("transform", "translateX(0px)")
            }
    }
}